define((require) => {
    "use strict";

    const _                     = require("underscore");
    const Browser               = require("shared/services/Browser");
    const Clipboard             = require("services/Clipboard");
    const EditorActivityManager = require("editor/ui/experience/editor/activity/EditorActivityManager");
    const LoggedIn              = require("shared/security/LoggedIn");
    const {Logger}              = require("shared/util/Logger");
    const Marionette            = require("marionette");
    const WidgetGroup           = require("ui/editor/WidgetGroup");

    const menuItemTpl = _.template("<%- label %> <span class=\"shortcut\"><%- shortcut %></span>");

    const SELECTORS = [
        ".widgets-container",  // scene context menu
        ".border"              // widget context menu (only when a widget is selected)
    ];

    // change to any of the following model's attribute must rebuild the context menu
    const VIEW_MODEL_CHANGES = "change:rulersShown change:snappingEnabled change:showMasterLayers";

    const log = new Logger("EditorContextMenu");

    /**
     * Context Menu for each scene
     */
    return Marionette.Controller.extend({

        // the scene to which this context menu applies
        _editorView: null,

        initialize(editorView) {
            this._editorView = editorView;

            this.listenTo(this._editorView, "render", this.create);
        },

        create() {
            const menu = this.getMenuCollection();
            const viewModel = this._editorView.viewScope.viewModel;

            /*
             * scene context menu
             */
            SELECTORS.forEach((selector) => this._editorView.$el.contextMenu({
                selector,
                items: menu,
                reposition: false
            }));

            // The context menu has to be rebuild when one of these properties are toggled because
            // the text changes.
            this.listenToOnce(viewModel, VIEW_MODEL_CHANGES, this.rebuild);
        },

        rebuild() {
            const viewModel = this._editorView.viewScope.viewModel;
            // Remove old listeners
            this.stopListening(viewModel, VIEW_MODEL_CHANGES);

            // Remove previously configured menus
            // jQuery ContextMenu's `destroy` command has to be called in
            // context of selectors menu was created (see `create()` method above for these selectors)
            this._editorView.$(SELECTORS.join(",")).contextMenu("destroy");

            this.create();
        },

        /**
         * Returns an object containing all content for the context menu.
         * Every item-key has an object-value with a name {String}, callback {Function call}
         * and disabled {Boolean} (disabled is optional).
         *
         * @return {Object} Object containing all items for the context menu.
         */
        getMenuCollection() {
            const viewModel = this._editorView.viewScope.viewModel;
            const zoomModel = viewModel.get("zoomModel");
            const key = Browser.os.macOS ? "\u2318" : "Ctrl";
            const shift = "\u21E7";
            const dump = Logger.level() >= Logger.LOG && (LoggedIn.internal() || LoggedIn.ad());

            const menu = {
                "copy": {
                    name: menuItemTpl({label: "Copy", shortcut: key + "+C"}),
                    isHtmlName: true,
                    callback() {
                        Clipboard.copy();
                    },
                    disabled() {
                        return viewModel.selection.isEmpty();
                    }
                },
                "paste": {
                    name: menuItemTpl({label: "Paste", shortcut: key + "+V"}),
                    isHtmlName: true,
                    callback(key, options) {
                        const scene = options.$trigger;
                        const containerPosition = scene.offset();
                        const menuPosition = options.$menu.offset();

                        Clipboard.paste({
                            left: menuPosition.left - containerPosition.left,
                            top: menuPosition.top - containerPosition.top
                        });
                    },
                    disabled() {
                        const data = Clipboard.getData();
                        return !data || data.type !== Clipboard.DataTypes.WIDGET;
                    }
                },
                "delete": {
                    name: "Delete",
                    callback() {
                        viewModel.deleteSelection({
                            triggerOrigin: "DeleteChoiceMasterLayersMenu"
                        });
                    },
                    disabled() {
                        return viewModel.selection.isEmpty();
                    }
                },
                "undo": {
                    name: menuItemTpl({label: "Undo", shortcut: key + "+Z"}),
                    isHtmlName: true,
                    callback() {
                        EditorActivityManager.undo();
                    },
                    disabled() {
                        return !EditorActivityManager.canUndo();
                    }
                },
                "redo": {
                    name: menuItemTpl({label: "Redo", shortcut: key + "+Y"}),
                    isHtmlName: true,
                    callback() {
                        EditorActivityManager.redo();
                    },
                    disabled() {
                        return !EditorActivityManager.canRedo();
                    }
                },

                "separator1": "",

                "group": {
                    name: menuItemTpl({label: "Group", shortcut: key + "+G"}),
                    isHtmlName: true,
                    callback() {
                        WidgetGroup.group({eventProperties: {triggerOrigin: "EditorContextMenu"}});
                    },
                    disabled() {
                        return !viewModel.get("scene").canGroupWidgets(viewModel.selection.get("models"));
                    }
                },

                "ungroup": {
                    name: menuItemTpl({label: "Ungroup", shortcut: shift + "+" + key + "+G"}),
                    isHtmlName: true,
                    callback() {
                        WidgetGroup.ungroup({eventProperties: {triggerOrigin: "EditorContextMenu"}});
                    },
                    disabled() {
                        return !viewModel.get("scene").canUngroupWidgets(viewModel.selection.get("models"));
                    }
                },

                "separator2": "",

                "zoomIn": {
                    name: menuItemTpl({label: "Zoom In", shortcut: "+"}),
                    isHtmlName: true,
                    callback() {
                        zoomModel.scaleUp();
                    },
                    disabled() {
                        return zoomModel.isMaxScale();
                    }
                },
                "zoomOut": {
                    name: menuItemTpl({label: "Zoom Out", shortcut: "-"}),
                    isHtmlName: true,
                    callback() {
                        zoomModel.scaleDown();
                    },
                    disabled() {
                        return zoomModel.isMinScale();
                    }
                },
                "resetZoom": {
                    name: menuItemTpl({label: "Reset Zoom", shortcut: "0"}),
                    isHtmlName: true,
                    callback() {
                        zoomModel.reset();
                    },
                    disabled() {
                        return zoomModel.get("scale") === 1;
                    }
                },

                "separator3": "",

                "toggleMasterLayers": {
                    name: menuItemTpl({
                        label: (viewModel.get("showMasterLayers")) ? "Show Active Layer Only" : "Show All Layers",
                        shortcut: "M"
                    }),
                    isHtmlName: true,
                    callback() {
                        viewModel.set("showMasterLayers", !viewModel.get("showMasterLayers"), {triggerOrigin: "EditorContextMenu"});
                    }
                },

                "toggleRulers": {
                    name: (viewModel.get("rulersShown") ? "Hide" : "Show") + " Rulers and Guidelines",
                    callback() {
                        viewModel.set("rulersShown", !viewModel.get("rulersShown"), "EditorContextMenu");
                    }
                },

                "toggleSnapping": {
                    name: "Turn " + (viewModel.get("snappingEnabled") ? "Off" : "On") + " Snapping",
                    callback() {
                        viewModel.set("snappingEnabled", !viewModel.get("snappingEnabled"), "EditorContextMenu");
                    }
                }
            };

            // On request from JSB, add option to dump experience json from internal network to
            // console for easy debugging...
            if (dump) {
                _.extend(menu, {
                    "separator4": "",

                    "dump": {
                        name: "Dump Experience",
                        callback() {
                            const experience = viewModel.get("experience");
                            const clone = experience.clone({duplicate: true});

                            log.info("Experience model:      ", experience);
                            log.info("Experience json:       ", experience.toJSON());
                            log.info("Experience clone model:", clone);
                            log.info("Experience clone json: ", clone.toJSON());
                        }
                    }
                });
            }

            return menu;
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/editor/EditorContextMenu.js